/** Copy for the three solution subpages. Image sources and guide URLs stay in the pages. */
export const solutionLabels = {
  downloadGuide: "Download the guide",
  downloadChecklist: "Download the checklist",
  contact: "Contact us",

  sbti: {
    pageTitle: "SBTi end-to-end",
    heading: "Setting SBTi-compliant climate targets",
    bannerTitle: "We guide you through every step of your SBTi journey.",
    bannerText:
      "From carbon accounting to target setting, submission, and decarbonisation, the process is fully " +
      "supported. Built for the SBTi Net-Zero Standard V2.0, covering OER with carbon credits today and " +
      "removals from 2035.",
    bannerImageAlt: "Science Based Targets initiative and ClimatePartner logos",
    sectionHeading: "From carbon footprint to SBTi validation",
    items: [
      {
        title: "Calculating your carbon footprint",
        description:
          "We help you build an audit-ready carbon footprint across scopes 1, 2, and 3, based on " +
          "activity-based data and 50,000+ emission factors. Expert guidance at every step ensures your " +
          "outputs meet all SBTi requirements. AI-enabled software supports the process throughout.",
      },
      {
        title: "Setting and validating science-based targets",
        description:
          "We help you develop near-term, long-term & FLAG targets, aligned with SBTi NetZero 2.0 " +
          "criteria and built on your emissions data. We guide you through the submission process, from " +
          "documentation to responding to feedback.",
      },
      {
        title: "Turning targets into a concrete decarbonisation roadmap",
        description:
          "We help you identify your biggest emission sources, define and prioritise the most " +
          "cost-effective measures, and meet your targets. You get a clear action plan, timelines, " +
          "scenario modelling, and an output ready for management decisions.",
      },
      {
        title: "Reducing scope 2 emissions with Energy Attribute Certificates",
        description:
          "We help you source EACs across regions and time periods without changes to your existing " +
          "energy contracts. Procurement is flexible, audit-ready, and matches your reporting " +
          "requirements, disclosure needs, and timelines.",
      },
      {
        title: "Addressing ongoing emissions through climate projects",
        description:
          "Achieve formal SBTi recognition, including OER with certified carbon credits. With over 20 " +
          "years of experience, strong relationships with local partners, and rigorous quality " +
          "standards, we help you invest in projects that drive real impact.",
      },
    ],
  },

  scope3: {
    pageTitle: "Scope 3 decarbonisation",
    heading: "Scope 3: from estimates to primary data",
    bannerTitle: "Making scope 3 emissions visible",
    bannerText:
      "We help you collect supplier-specific emission data at scale, replacing estimates with primary " +
      "data and turning scope 3 from a reporting challenge into a decarbonisation lever.",
    bannerImageAlt: "A tablet showing the practical guide for scope 3 reduction",
    sectionHeading: "From carbon footprints to informed procurement decisions",
    items: [
      {
        title: "Identifying scope 3 hotspots",
        description:
          "We help you calculate an audit-ready carbon footprint. Analytics dashboards reveal your " +
          "biggest scope 3 emission sources and reduction measures with the most impact.",
      },
      {
        title: "Collecting primary PCF data from suppliers",
        description:
          "Scope 3 hotspots are often based on secondary data and assumptions. With Network, you " +
          "exchange structured PCF data with suppliers, replacing assumptions with real numbers. Every " +
          "PCF flows directly into your scope 3 footprint, with progress and data gaps visible at a " +
          "glance.",
      },
      {
        title: "Enabling suppliers",
        description:
          "Many suppliers lack the resources or expertise to calculate PCFs. Without active support, " +
          "data requests go unanswered. We guide your suppliers through structured training " +
          "programmes, targeted 1:1 support, and expert-led workshops.",
      },
      {
        title: "Turning your data into a decarbonisation roadmap",
        description:
          "We help you track reductions over time and translate your data into a concrete action plan. " +
          "Cost and impact scenarios help you prioritise the right measures across your value chain — " +
          "with a clear, decision-ready output.",
      },
      {
        title: "Reducing scope 2 emissions with Energy Attribute Certificates",
        description:
          "Source EACs without changes to your existing energy contracts. RE100/SBTi-compliant, with " +
          "expert guidance on communication.",
      },
      {
        title: "Reaching farm level and extending upstream action across the full value chain",
        description:
          "We help you scale scope 3 decarbonisation beyond tier 1 — with farm-level emission " +
          "calculations that create measurable impact where emissions are highest.",
      },
      {
        title: "Addressing ongoing emissions through climate projects",
        description:
          "Support certified climate projects from a regulation-ready portfolio — backed by a rigorous " +
          "360-data-point due diligence framework. Aligned with SBTi and CSRD requirements, with active " +
          "portfolio management and recurring integrity screenings to keep your climate story credible " +
          "over time.",
      },
    ],
  },

  empco: {
    pageTitle: "EmpCo-compliant labels",
    heading: "Communicate climate action EmpCo-compliant",
    bannerTitle: "Your path to an EmpCo-compliant label",
    bannerText:
      "From 27 September 2026, new rules apply to green claims and sustainability labels. With " +
      "ClimatePartner, you communicate your climate action in compliance with EmpCo.",
    bannerImageAlt: "A bottle carrying a ClimatePartner certified product label",
    sectionHeading: "One solution to measure, reduce, and prove your climate action",
    items: [
      {
        title: "Calculate your carbon footprint at company or product level",
        description:
          "We help you calculate a GHG Protocol-aligned Corporate Carbon Footprint or Product Carbon " +
          "Footprint, giving you a reliable, audit-ready basis for your reduction strategy and " +
          "reporting obligations.",
      },
      {
        title: "Create an EmpCo-ready reduction implementation plan",
        description:
          "We help you create a structured reduction plan based on your carbon footprint, covering " +
          "pre-defined measures prioritised by expected emission impact. Publicly accessible and " +
          "verifiable, it keeps your climate targets credible and compliant with current and upcoming " +
          "regulation.",
      },
      {
        title: "Finance climate projects",
        description:
          "Support certified climate and biodiversity projects that contribute to global climate " +
          "action beyond your own value chain. Transparent project pages make your contribution " +
          "traceable and tell a credible story to your customers and stakeholders.",
      },
      {
        title: "Receive an EmpCo-compliant Label",
        description:
          'Receive the "ClimatePartner certified" (CPC) or "Financial Climate Contribution" (FCC) ' +
          "label - EmpCo-compliant, with independent third-party verification by an accredited " +
          "certification body (e.g. DEKRA, TÜV). And with our ClimatePartner certified label, you " +
          "qualify for Amazon Climate Pledge Friendly (up to +12% revenue uplift).",
      },
      {
        title: "Communicate transparently",
        description:
          "Make your climate action visible and traceable through an individual transparency page. " +
          "Clear, evidence-based communication builds trust with consumers, supports your brand, and " +
          "ensures compliance with regulatory requirements on environmental claims.",
      },
    ],
  },
};
